// src/components/molecules/DateFilter/DateFilter.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
import { operationalScrollbars } from '../../../design-system/tokens';

/**
 * DateFilter - Componente molecular para filtro de data
 * 
 * Este componente permite selecionar opções de filtro de data.
 * Segue o princípio do Atomic Design como uma "molécula".
 */
const DateFilter = ({
  value,
  onChange,
  options = [
    { value: '24h', label: 'Últimas 24h' },
    { value: '3d', label: 'Últimos 3 dias' },
    { value: '7d', label: 'Últimos 7 dias' },
    { value: '30d', label: 'Últimos 30 dias' },
    { value: 'custom', label: 'Personalizado' }
  ],
  className = '',
  size = 'small',
  ...props
}) => {
  const [showCustomModal, setShowCustomModal] = useState(false);

  const handleSelectChange = (selectedValue) => {
    if (selectedValue === 'custom') {
      // Se selecionou "Personalizado", abrir o modal automaticamente
      setShowCustomModal(true);
    } else {
      onChange(selectedValue, null);
    }
  };

  const handleCustomDateChange = (dateRange) => {
    onChange('custom', dateRange);
    setShowCustomModal(false); // Fechar modal após seleção
  };

  const handleCloseModal = () => {
    setShowCustomModal(false);
    // Se estava em "custom" mas cancelou, voltar para vazio
    if (value === 'custom') {
      onChange('', null);
    }
  };

  const getDisplayValue = () => {
    if (!value) return 'Data';
    
    if (value === 'custom') {
      return 'Personalizado';
    }
    
    const option = options.find(opt => opt.value === value);
    return option ? option.label : 'Data';
  };

  return (
    <>
      <FormControl size={size} className={`date-filter ${className}`} sx={{ minWidth: 140 }} {...props}>
        <Select
          displayEmpty
          value={value || ''}
          onChange={(e) => handleSelectChange(e.target.value)}
          renderValue={(selected) => {
            return getDisplayValue();
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
                ...operationalScrollbars.default,
              },
            },
          }}
        >
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Modal para seleção de data personalizada */}
      {showCustomModal && (
        <DateRangePicker
          value={null}
          onChange={handleCustomDateChange}
          onClose={handleCloseModal}
          size={size}
          label="Período Personalizado"
        />
      )}
    </>
  );
};

DateFilter.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  className: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium'])
};

export default DateFilter;
